// ENG2-2455 (T4, Part B): "Apply <package> defaults?" when an admin moves a
// customer onto a 2026 package.
//
// RisikaAPI keeps a customer's restriction_exceptions row across a plan
// change, so a customer moved from TRIAL_V2 (lookups raised to 10,000 by CS)
// to MANUAL_PACKAGE would keep 10,000. Applying the defaults sends one
// POST /company/update with `null` for every exception column that has a
// template counterpart; update_restrictions turns each null into SQL NULL so
// the template wins on the refresh that follows.
//
// Kept on purpose (contract data, not package defaults): allow_zones,
// global_lookups_limit, rights.global.global_data, the two global data
// subscription dates, allow_ai (Risika for AI, ENG2-2542 / D5: changing plan
// must not switch it off) and allow_sales_triggers (Sales Signals, ENG2-2815,
// for the same reason).
//
// The Risika for AI end date (rights.global.ai_end_date, ENG2-2614) is not
// sent either: RisikaAPI owns it on a plan change — cleared, then filled with
// today + 14 when the new plan is TRIAL_MCP. CMD re-fetches the customer after
// the change (useUpdateCustomer invalidates the plan query) so the cleared or
// re-filled date shows. TRIAL_MCP_DEFAULTS below is what the API will store,
// shown to CS before they confirm.
//
// Countries are reset as a whole (`DK: null`) rather than
// `DK: { score_models: null }`: merge_exceptions pops a country that is sent
// as null, whereas a null score_models list on a customer with no DK
// exception would be stored and then iterated by calculate_new_rights.

import { ACTIVE_SUBSCRIPTION_PLANS } from 'types'

// The TRIAL_MCP defaults (lookups 200, Risika for AI on, end date today + 14)
// live in utils/trial-mcp.ts so the Create Customer dialog can show them too.
export {
  TRIAL_MCP_DEFAULTS,
  TRIAL_MCP_PLAN,
  packageDefaultsNote,
  trialMcpEndDate,
} from 'utils/trial-mcp'

export const APPLY_DEFAULTS_NULL_KEYS = [
  'max_users',
  'lookups',
  'credit_ratings',
  'credit_reports',
  'monitoring_count',
  'monitoring_exports',
  'search_exports',
  'search_exports_limit',
  'tier',
  'allow_api',
  'allow_basic',
  'allow_browser_extension',
  'allow_company_export',
  'allow_company_search',
  'allow_countries',
  'allow_credit',
  'allow_credit_policy',
  'allow_ean',
  'allow_financial',
  'allow_hierarchy',
  'allow_highlights',
  'allow_monitoring',
  'allow_monitoring_export',
  'allow_notes',
  'allow_pdf_reports',
  'allow_person_export',
  'allow_person_search',
  'allow_relations',
] as const

export const APPLY_DEFAULTS_GLOBAL_FLAGS = [
  'observational_lists',
  'enterprise_credit_policy',
  'fraud_indicators',
  'credit_override',
  'beneficial_owners',
] as const

export const APPLY_DEFAULTS_COUNTRIES = ['DK', 'SE', 'NO', 'FI'] as const

export type ApplyDefaultsPayload = {
  subscription_plan: string
  rights: {
    global: Record<(typeof APPLY_DEFAULTS_GLOBAL_FLAGS)[number], null>
  } & Record<(typeof APPLY_DEFAULTS_COUNTRIES)[number], null>
} & Record<(typeof APPLY_DEFAULTS_NULL_KEYS)[number], null>

export const needsApplyDefaultsPrompt = (
  currentPlan: string | undefined | null,
  newPlan: string,
): boolean =>
  newPlan !== currentPlan &&
  (ACTIVE_SUBSCRIPTION_PLANS as readonly string[]).includes(newPlan)

export const buildApplyDefaultsPayload = (
  newPlan: string,
): ApplyDefaultsPayload => {
  const nulls = Object.fromEntries(
    APPLY_DEFAULTS_NULL_KEYS.map((key) => [key, null]),
  ) as Record<(typeof APPLY_DEFAULTS_NULL_KEYS)[number], null>
  const global = Object.fromEntries(
    APPLY_DEFAULTS_GLOBAL_FLAGS.map((flag) => [flag, null]),
  ) as Record<(typeof APPLY_DEFAULTS_GLOBAL_FLAGS)[number], null>
  const countries = Object.fromEntries(
    APPLY_DEFAULTS_COUNTRIES.map((country) => [country, null]),
  ) as Record<(typeof APPLY_DEFAULTS_COUNTRIES)[number], null>

  return {
    subscription_plan: newPlan,
    ...nulls,
    rights: { global, ...countries },
  }
}
